// Final Updated Version of WaitingForOpponentScreen.tsx

import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
} from "react-native";

import { t } from '../../../i18n/translations';
import type { RootStackParamList } from "../../../navigation/types";
import { supabase } from "../../../shared/services/supabaseService";
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useUserProfile } from "../../quiz-core/useStore";
import MultiplayerActionButton from "../components/MultiplayerActionButton";
import PlayerResultsCard from "../components/PlayerResultsCard";
import PlayerStatusList from "../components/PlayerStatusList";
import { useSessionKeepalive } from '../hooks/useSessionKeepalive';

const IQES = {
  background: "#090317",
  panel: "#0B1026",
  panelDeep: "#070A18",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purple: "#4B1F78",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  borderStrong: "rgba(248, 200, 78, 0.72)",
  green: "#7FE08E",
  greenDeep: "#0F2F1F",
  red: "#EF6B63",
};

const PlayerResultsCardWrapper = ({
  score,
  totalQuestions,
  finished,
  roomId,
}: {
  score?: number;
  totalQuestions?: number;
  finished?: boolean;
  roomId: string;
}) => {
  const profile = useUserProfile();
  const { language } = useLanguageStore();
  const [player, setPlayer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchPlayer = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("multiplayer_game_room_players")
        .select("score, total_questions, finished_at")
        .eq("room_id", roomId)
        .eq("user_id", profile?.id)
        .single();
      if (!mounted) return;
      setPlayer(data);
      setLoading(false);
    };

    if (profile?.id && roomId) fetchPlayer();
    const interval = setInterval(fetchPlayer, 3000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [roomId, profile?.id]);

  const showCard = player || (typeof score === "number" && typeof totalQuestions === "number");

  return (
    <View
      style={styles.resultsCardContainer}
    >
      <View style={{ position: "relative", width: "100%", alignItems: "center", justifyContent: "center" }}>
        {loading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={IQES.gold} />
            <Text style={styles.loadingText}>{t('multiplayer_waiting.loadingResults', language)}</Text>
          </View>
        )}

        {!loading && showCard ? (
          player && player.finished_at ? (
            <PlayerResultsCard
              score={player.score}
              totalQuestions={player.total_questions}
              accuracy={Math.round((player.score / player.total_questions) * 100)}
            />
          ) : finished ? (
            <PlayerResultsCard
              score={score ?? 0}
              totalQuestions={totalQuestions ?? 0}
              accuracy={totalQuestions ? Math.round(((score ?? 0) / totalQuestions) * 100) : 0}
            />
          ) : (
            <Text style={styles.waitingText}>{t('multiplayer_waiting.waitingToFinish', language)}</Text>
          )
        ) : null}
      </View>
    </View>
  );
};

async function resolveResultsSessionId(
  roomId: string,
  preferredSessionId?: string | null,
) {
  if (preferredSessionId) {
    return preferredSessionId;
  }

  const { data: roomData } = await supabase
    .from("multiplayer_game_rooms")
    .select("current_session_id")
    .eq("id", roomId)
    .maybeSingle();

  if (roomData?.current_session_id) {
    return roomData.current_session_id;
  }

  const { data: latestSession } = await supabase
    .from("multiplayer_game_sessions")
    .select("id")
    .eq("room_id", roomId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  return latestSession?.id ?? `room-${roomId}`;
}

export default function WaitingForOpponentScreen() {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "WaitingForOpponent">>();
  const {
    roomId,
    remainingTime,
    familyId,
    familyName,
    friendsGroupId,
    friendsGroupName,
  } = route.params;
  
  // Keep session alive during waiting
  useSessionKeepalive(true);
  
  // Enhanced state for real-time sync
  const [playersFinished, setPlayersFinished] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [masterTimer, setMasterTimer] = useState<number>(remainingTime || 200); // Display only - not used for timing logic
  const [totalGameTime, setTotalGameTime] = useState<number>(200); // Default to medium difficulty
  const [gamePhase, setGamePhase] = useState<'playing' | 'completed'>('playing');
  const [allPlayersFinished, setAllPlayersFinished] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [difficulty, setDifficulty] = useState<string>('easy');
  
  // NEW: Timestamp-based timer approach using refs (avoids React render cycles)
  const timerStartTime = useRef<number | null>(null);
  const initialDuration = useRef<number | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerInitialized = useRef<boolean>(false);
  const hasNavigatedToResultsRef = useRef<boolean>(false);
  
  // Personal results data
  const [personalScore, setPersonalScore] = useState<number>(0);
  const [personalAccuracy, setPersonalAccuracy] = useState<number>(0);
  const [completionTime, setCompletionTime] = useState<string>('');
  
  // User profile for host detection
  const profile = require('../../quiz-core/useStore').useUserProfile();
  const { language } = useLanguageStore();

  // NEW: Initialize timestamp-based timer (only once)
  useEffect(() => {
    if (remainingTime !== undefined && remainingTime >= 0 && !timerInitialized.current) {
      // Set up timing references
      timerStartTime.current = Date.now();
      initialDuration.current = remainingTime;
      timerInitialized.current = true;
      
      // Start the display update interval immediately
      startTimerDisplay();
    }
  }, [remainingTime]);

  useEffect(() => {
    let mounted = true;

    const navigateToResults = async () => {
      if (hasNavigatedToResultsRef.current) return;
      hasNavigatedToResultsRef.current = true;

      try {
        const { currentSession } = require("../../quiz-core/multiplayerStore").useMultiplayerStore.getState();
        const sessionId = await resolveResultsSessionId(roomId, currentSession?.id);

        navigation.replace("MultiplayerResults", {
          roomId,
          sessionId,
          isHost,
          familyId,
          familyName,
          friendsGroupId,
          friendsGroupName,
        });
      } catch (error) {
        // Error navigating to results
      }
    };

    const fetchGameState = async () => {
      if (!mounted) return;

      try {
        // Fetch room data and game state
        const [roomResponse, playersResponse] = await Promise.all([
          supabase
            .from("multiplayer_game_rooms")
            .select("host_id, difficulty, game_start_time, game_duration_seconds, status, current_session_id")
            .eq("id", roomId)
            .single(),
          supabase
            .from("multiplayer_game_room_players")
            .select("finished_at, user_id, score, total_questions")
            .eq("room_id", roomId)
        ]);

        if (!mounted) return;

        if (roomResponse.data && playersResponse.data) {
          const room = roomResponse.data;
          const players = playersResponse.data;
          
          // Set room info
          const roomDifficulty = room.difficulty || 'easy';
          setDifficulty(roomDifficulty);
          setIsHost(room.host_id === profile?.id);
          
          // Calculate total game time for progress bar display only
          const gameTime = roomDifficulty.toLowerCase() === 'easy' ? 305 : 
                          roomDifficulty.toLowerCase() === 'medium' ? 205 : 105;
          setTotalGameTime(gameTime);
          
          // NEW: Only initialize timer if not already done (polling never touches timer)
          if (!timerInitialized.current) {
            if (remainingTime !== undefined && remainingTime >= 0) {
              // Timer initialization happens in the dedicated useEffect above
            } else if (room.game_start_time && room.status === 'in_progress') {
              // Fallback: calculate from game start time and initialize
              const startTime = new Date(room.game_start_time).getTime();
              const elapsed = Math.floor((Date.now() - startTime) / 1000);
              const remaining = Math.max(0, gameTime - elapsed);
              timerStartTime.current = Date.now();
              initialDuration.current = remaining;
              timerInitialized.current = true;
              startTimerDisplay();
              
              if (remaining <= 0) {
                setGamePhase('completed');
                navigateToResults();
                return;
              }
            }
          }
          // CRITICAL: Polling NEVER modifies timer after initialization
          
          // Player completion tracking
          const finished = players.filter((p: any) => p.finished_at).length;
          setPlayersFinished(finished);
          setTotalPlayers(players.length);
          const everyoneFinished = finished >= players.length && players.length > 0;
          setAllPlayersFinished(everyoneFinished);
          
          // Get personal results
          const currentPlayer = players.find((p: any) => p.user_id === profile?.id);
          if (currentPlayer && currentPlayer.finished_at) {
            setPersonalScore(currentPlayer.score || 0);
            setPersonalAccuracy(Math.round(((currentPlayer.score || 0) / (currentPlayer.total_questions || 10)) * 100));
            
            // Calculate completion time
            if (room.game_start_time) {
              const startTime = new Date(room.game_start_time).getTime();
              const finishTime = new Date(currentPlayer.finished_at).getTime();
              const timeTaken = Math.floor((finishTime - startTime) / 1000);
              const minutes = Math.floor(timeTaken / 60);
              const seconds = timeTaken % 60;
              setCompletionTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
            }
          }

          if (room.status === 'completed' || everyoneFinished) {
            setGamePhase('completed');
            navigateToResults();
            return;
          }
        }
      } catch (error) {
        // Error fetching game state
      }
    };

    // Initial fetch
    fetchGameState();
    
    // Set up polling interval - less frequent to avoid interfering with countdown
    const interval = setInterval(fetchGameState, 5000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [
    roomId,
    navigation,
    profile?.id,
    familyId,
    familyName,
    friendsGroupId,
    friendsGroupName,
    isHost,
  ]); // Timer initialization is handled separately

  // NEW: Timestamp-based timer display function (no React dependencies)
  // END GAME FOR ALL PLAYERS when timer reaches 0
  const endGameForAllPlayers = async () => {
    try {
      // 1. Update room status to completed in database
      const { error: roomError } = await supabase
        .from('multiplayer_game_rooms')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', roomId);
      
      // Room status update handled
      
      // 2. Call server function to end game and force-submit scores for active players
      try {
        const { data: endGameData, error: endGameError } = await supabase.rpc('force_end_multiplayer_game', {
          room_id_param: roomId
        });
        
        // Force-end game handled
      } catch (rpcError) {
        // RPC call failed, continuing with navigation
        // Continue even if RPC fails - we can still show results
      }
      
      // 3. Navigate to results after short delay
      setTimeout(() => {
        void (async () => {
          const { currentSession } = require("../../quiz-core/multiplayerStore").useMultiplayerStore.getState();
          const sessionId = await resolveResultsSessionId(roomId, currentSession?.id);

          navigation.replace("MultiplayerResults", {
            sessionId,
            roomId: roomId,
            isHost: isHost,
            familyId,
            familyName,
            friendsGroupId,
            friendsGroupName,
          });
        })();
      }, 2000); // 2 second delay to allow server updates to propagate
      
    } catch (error) {
      // Error ending game, trying fallback navigation
      // Even if there's an error, try to navigate to results
      setTimeout(() => {
        void (async () => {
          const sessionId = await resolveResultsSessionId(roomId);
          navigation.replace("MultiplayerResults", {
            roomId: roomId,
            sessionId,
            isHost: isHost,
            familyId,
            familyName,
            friendsGroupId,
            friendsGroupName,
          });
        })();
      }, 1000);
    }
  };

  const startTimerDisplay = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      if (!timerStartTime.current || !initialDuration.current) {
        return;
      }

      // Calculate elapsed time and remaining time
      const elapsed = Math.floor((Date.now() - timerStartTime.current) / 1000);
      const remaining = Math.max(0, initialDuration.current - elapsed);
      
      // Update display state
      setMasterTimer(remaining);
      
      // Handle game completion
      if (remaining <= 0) {
        // Clear interval immediately
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        setGamePhase('completed');
        
        // CRITICAL: End the game server-side for ALL players
        endGameForAllPlayers();
      }
    }, 1000);
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        // Cleaning up timer on unmount
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Early end game functionality
  const handleEarlyEndGame = async () => {
    if (!isHost || !allPlayersFinished) return;
    
    try {
      // End the game early by updating room status
      await supabase
        .from('multiplayer_game_rooms')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', roomId);
      
      // Navigate to results immediately
      const { currentSession } = require("../../quiz-core/multiplayerStore").useMultiplayerStore.getState();
      const sessionId = await resolveResultsSessionId(roomId, currentSession?.id);
      
      navigation.replace("MultiplayerResults", {
        roomId,
        sessionId,
        isHost,
        familyId,
        familyName,
        friendsGroupId,
        friendsGroupName,
      });
    } catch (error) {
      // Error ending game early
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View pointerEvents="none" style={styles.ambientOrbTop} />
      <View pointerEvents="none" style={styles.ambientOrbBottom} />
      <View pointerEvents="none" style={styles.sparkOne} />
      <View pointerEvents="none" style={styles.sparkTwo} />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <View
            style={styles.heroCard}
          >
            <Text style={styles.heroEyebrow}>
              {t('multiplayer_waiting.waitingForPlayers', language)}
            </Text>
            <Text style={styles.heading}>
              {allPlayersFinished ? t('multiplayer_waiting.allPlayersFinished', language) : t('multiplayer_waiting.youFinished', language)}
            </Text>
            <Text style={styles.subheading}>
              {allPlayersFinished
                ? t('multiplayer_waiting.resultsAlmostReady', language)
                : t('multiplayer_waiting.waitingForTimer', language)}
            </Text>
          </View>

          {/* Master Timer Display - ALWAYS VISIBLE */}
          <View style={styles.masterTimerContainer}>
            <Text style={styles.masterTimerTitle}>{t('multiplayer_waiting.gameTimeRemaining', language)}</Text>
            <Text style={styles.masterTimerText}>
              {Math.floor(masterTimer / 60)}:{(masterTimer % 60).toString().padStart(2, '0')}
            </Text>
            <View style={styles.masterTimerBar}>
              <View 
                style={[
                  styles.masterTimerFill, 
                  { width: `${Math.max(0, (masterTimer / totalGameTime) * 100)}%` }
                ]} 
              />
            </View>
            <Text style={styles.masterTimerSubtext}>
              {t('multiplayer_waiting.allPlayersEndSimultaneously', language)}
            </Text>
            <View style={styles.difficultyBadge}>
              <Text style={styles.difficultyText}>
                {difficulty.toUpperCase()}
              </Text>
              <Text style={styles.difficultySubtext}>
                {Math.floor(totalGameTime / 60)}:{(totalGameTime % 60).toString().padStart(2, '0')} TOTAL
              </Text>
            </View>
          </View>

          {/* Personal Results Preview */}
          {personalScore > 0 && (
            <View style={styles.personalResultsContainer}>
              <Text style={styles.personalResultsTitle}>{t('multiplayer_waiting.yourResults', language)}</Text>
              <View style={styles.personalResultsContent}>
                <View style={styles.personalResultItem}>
                  <Text style={styles.personalResultValue}>{personalScore}/10</Text>
                  <Text style={styles.personalResultLabel}>{t('multiplayer_waiting.correct', language)}</Text>
                </View>
                <View style={styles.personalResultItem}>
                  <Text style={styles.personalResultValue}>{personalAccuracy}%</Text>
                  <Text style={styles.personalResultLabel}>{t('multiplayer_waiting.accuracy', language)}</Text>
                </View>
                <View style={styles.personalResultItem}>
                  <Text style={styles.personalResultValue}>{completionTime}</Text>
                  <Text style={styles.personalResultLabel}>{t('multiplayer_waiting.time', language)}</Text>
                </View>
              </View>
            </View>
          )}

          {/* Player Progress Status */}
          {totalPlayers > 0 && (
            <View style={styles.progressContainer}>
              <Text style={styles.progressTitle}>{t('multiplayer_waiting.playerProgress', language)}</Text>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { width: `${(playersFinished / totalPlayers) * 100}%` }
                  ]} 
                />
              </View>
              <Text style={styles.progressText}>
                {allPlayersFinished
                  ? t('multiplayer_waiting.resultsAlmostReady', language)
                  : t('multiplayer_waiting.playersFinished', language).replace('{{finished}}', playersFinished.toString()).replace('{{total}}', totalPlayers.toString())}
              </Text>
            </View>
          )}

          {/* Early End Option (Host Only) */}
          {isHost && allPlayersFinished && masterTimer > 30 && !hasNavigatedToResultsRef.current && (
            <View style={styles.earlyEndContainer}>
              <Text style={styles.earlyEndTitle}>
                {t('multiplayer_waiting.everyoneFinishedEarly', language)}
              </Text>
              <TouchableOpacity 
                style={styles.earlyEndButton}
                onPress={handleEarlyEndGame}
              >
                <Text style={styles.earlyEndButtonText}>
                  {t('multiplayer_waiting.endGameNow', language)}
                </Text>
              </TouchableOpacity>
              <Text style={styles.earlyEndSubtext}>
                {t('multiplayer_waiting.waitForAutoEnd', language).replace('{{time}}', `${Math.floor(masterTimer / 60)}:${(masterTimer % 60).toString().padStart(2, '0')}`)}
              </Text>
            </View>
          )}

          <PlayerResultsCardWrapper
            roomId={roomId}
            score={route.params?.score}
            totalQuestions={route.params?.totalQuestions}
            finished={route.params?.finished}
          />

          <View style={{ marginTop: 12, marginBottom: 20, width: '100%', alignItems: 'center' }}>
            <PlayerStatusList roomId={roomId} />
          </View>

          {/* Dynamic Tips */}
          <View style={styles.tipsContainer}>
            <Text style={styles.tipsTitle}>{t('multiplayer_waiting.whileYouWait', language)}</Text>
            <Text style={styles.tipsText}>
              {allPlayersFinished 
                ? t('multiplayer_waiting.realTimeFairScoring', language)
                : masterTimer > 60
                ? t('multiplayer_waiting.othersStillAnswering', language)
                : t('multiplayer_waiting.gameEndingSoon', language)}
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: IQES.background,
  },
  ambientOrbTop: {
    position: "absolute",
    top: -120,
    right: -105,
    width: 250,
    height: 250,
    borderRadius: 125,
    backgroundColor: "rgba(91, 44, 131, 0.38)",
  },
  ambientOrbBottom: {
    position: "absolute",
    bottom: -155,
    left: -125,
    width: 270,
    height: 270,
    borderRadius: 135,
    backgroundColor: "rgba(248, 200, 78, 0.11)",
  },
  sparkOne: {
    position: "absolute",
    top: "31%",
    right: "20%",
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: IQES.goldSoft,
    opacity: 0.72,
  },
  sparkTwo: {
    position: "absolute",
    bottom: "22%",
    left: "27%",
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: IQES.gold,
    opacity: 0.55,
  },
  container: {
    width: "100%",
    maxWidth: 420,
    marginHorizontal: "auto",
    alignItems: "center",
    paddingTop: Platform.OS === "web" ? 40 : 16,
    paddingHorizontal: 16,
    zIndex: 2,
  },
  heroCard: {
    width: "100%",
    maxWidth: 340,
    borderRadius: 28,
    borderWidth: 1,
    paddingHorizontal: 22,
    paddingVertical: 24,
    marginTop: 8,
    marginBottom: 18,
    alignItems: "center",
    backgroundColor: IQES.panel,
    borderColor: IQES.borderStrong,
    shadowColor: IQES.gold,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 22,
    elevation: 7,
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 8,
    color: IQES.goldSoft,
  },
  heading: {
    fontSize: 34,
    fontWeight: "800",
    textAlign: "center",
    marginBottom: 8,
    letterSpacing: -0.4,
    color: IQES.ivory,
  },
  subheading: {
    fontSize: 17,
    textAlign: "center",
    marginBottom: 0,
    fontWeight: "500",
    lineHeight: 24,
    color: IQES.muted,
  },
  resultsCardContainer: {
    width: "100%",
    maxWidth: 340,
    minHeight: 220,
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
    borderRadius: 18,
    overflow: "hidden",
    borderWidth: 1,
    paddingVertical: 16,
    backgroundColor: IQES.panelDeep,
    borderColor: IQES.border,
  },
  waitingText: {
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
    color: IQES.text,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 20,
    backgroundColor: "rgba(7, 10, 24, 0.9)",
  },
  loadingText: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
    color: IQES.text,
  },
  progressContainer: {
    width: "100%",
    maxWidth: 320,
    marginBottom: 24,
    alignItems: "center",
    borderRadius: 20,
    borderWidth: 1,
    padding: 18,
    backgroundColor: IQES.panel,
    borderColor: IQES.border,
  },
  progressBar: {
    width: "100%",
    height: 8,
    backgroundColor: "rgba(255, 247, 223, 0.12)",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 12,
  },
  progressFill: {
    height: "100%",
    backgroundColor: IQES.gold,
    borderRadius: 4,
  },
  progressText: {
    color: IQES.text,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  estimatedTimeText: {
    color: IQES.goldSoft,
    fontSize: 14,
    fontWeight: "500",
  },
  tipsContainer: {
    width: "100%",
    maxWidth: 320,
    backgroundColor: IQES.panelDeep,
    borderRadius: 12,
    padding: 16,
    marginTop: 20,
    marginBottom: 40,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  tipsTitle: {
    color: IQES.goldSoft,
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 8,
    textAlign: "center",
  },
  tipsText: {
    color: IQES.muted,
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
    lineHeight: 20,
  },
  masterTimerContainer: {
    width: "100%",
    maxWidth: 340,
    backgroundColor: IQES.panel,
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
    alignItems: "center",
    borderWidth: 1,
    borderColor: IQES.border,
    shadowColor: IQES.gold,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.14,
    shadowRadius: 8,
    elevation: 3,
  },
  masterTimerTitle: {
    color: IQES.text,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
  },
  masterTimerText: {
    color: IQES.goldSoft,
    fontSize: 36,
    fontWeight: "800",
    marginBottom: 16,
    letterSpacing: 1,
  },
  masterTimerBar: {
    width: "100%",
    height: 12,
    backgroundColor: "rgba(255, 247, 223, 0.12)",
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: 12,
  },
  masterTimerFill: {
    height: "100%",
    backgroundColor: IQES.gold,
    borderRadius: 6,
  },
  masterTimerSubtext: {
    color: IQES.muted,
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
    marginBottom: 8,
  },
  difficultyBadge: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: IQES.purpleDeep,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  difficultyText: {
    color: IQES.goldSoft,
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: 0.8,
  },
  difficultySubtext: {
    color: IQES.muted,
    fontSize: 12,
    fontWeight: "600",
  },
  personalResultsContainer: {
    width: "100%",
    maxWidth: 340,
    backgroundColor: IQES.panel,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: IQES.border,
    shadowColor: IQES.gold,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 3,
  },
  personalResultsTitle: {
    color: IQES.ivory,
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 16,
  },
  personalResultsContent: {
    flexDirection: "row",
    justifyContent: "space-around",
  },
  personalResultItem: {
    alignItems: "center",
  },
  personalResultValue: {
    color: IQES.goldSoft,
    fontSize: 24,
    fontWeight: "800",
    marginBottom: 4,
  },
  personalResultLabel: {
    color: IQES.muted,
    fontSize: 12,
    fontWeight: "600",
  },
  progressTitle: {
    color: IQES.ivory,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
    textAlign: "center",
  },
  earlyEndContainer: {
    width: "100%",
    maxWidth: 340,
    backgroundColor: IQES.greenDeep,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(127, 224, 142, 0.38)",
  },
  earlyEndTitle: {
    color: IQES.ivory,
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 16,
  },
  earlyEndButton: {
    backgroundColor: IQES.gold,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#1D1A23",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  earlyEndButtonText: {
    color: IQES.purpleDeep,
    fontSize: 16,
    fontWeight: "700",
    textAlign: "center",
  },
  earlyEndSubtext: {
    color: IQES.muted,
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
  },
});
